import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  TextField,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  AutoAwesome as AiIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import StatusPill from "../components/common/StatusPill";
import MyMagicPanelShell from "../shared/MyMagicPanelShell";
import { getCompanyKnowledge, saveCompanyKnowledge } from "../utils/voiceProfileDB";
import { apiGetDomainByName, apiUpdateDomainMetadata, apiUpdateDomainAvatar } from "../../../graphQL/domain";
import { apiUploadFileToS3 } from "../../../graphQL/file";
import { v4 as uuid_v4 } from "../../../libs/uuid";
import config from "../../../config";
import ThumbnailUploadWithEditor from "../../../components/upload/ThumbnailUploadWithEditor";

const sx = {
  empty: { py: 4, display: "flex", justifyContent: "center" },
  cardList: { display: "flex", flexDirection: "column", gap: 1 },
  card: (selected) => (theme) => ({
    border: `1px solid ${selected ? "#7c3aed" : theme.palette.divider}`,
    borderRadius: 2,
    p: 1.5,
    backgroundColor: selected ? "#F5F3FF" : theme.palette.background.paper,
    borderLeft: "3px solid",
    borderLeftColor: selected ? "#7c3aed" : "divider",
    cursor: "pointer",
    "&:hover": { boxShadow: "0 1px 4px rgba(0,0,0,0.08)", backgroundColor: selected ? "#F5F3FF" : "#F9FAFB" },
    transition: "all 0.15s ease",
  }),
  cardName: { fontSize: 14, fontWeight: 700, color: "text.primary" },
  cardMeta: { fontSize: 12, color: "text.secondary", lineHeight: 1.3 },
  tagRow: { display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 },
  tag: {
    fontSize: 10,
    fontWeight: 500,
    px: 0.75,
    py: 0.125,
    borderRadius: "4px",
    backgroundColor: "#EDE9FE",
    color: "#7c3aed",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    mt: 0.75,
  },
};

const TONE_LABELS = {
  direct_warm: "Direct & Warm",
  polished_formal: "Polished & Formal",
  casual_playful: "Casual & Playful",
  text_friendly: "Friendly",
  text_professional: "Professional",
  text_urgent: "Urgent",
  text_playful: "Playful",
};

const ProfileCard = ({ profile, selected, onClick, onDelete }) => (
  <Box sx={sx.card(selected)} onClick={onClick}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AiIcon sx={{ fontSize: 14, color: "#7c3aed" }} />
          <Typography sx={sx.cardName}>{profile.name}</Typography>
        </Box>
        <Typography sx={sx.cardMeta}>
          {TONE_LABELS[profile.toneArchetype] || profile.toneArchetype} · Formality {profile.formality}/5
        </Typography>
      </Box>
      {profile.isStarter && <StatusPill status="starter" />}
    </Box>
    <Box sx={sx.tagRow}>
      <Box component="span" sx={sx.tag}>Contractions: {profile.contractions}</Box>
      <Box component="span" sx={sx.tag}>Length: {(profile.length || "").replace(/_/g, " ")}</Box>
      <Box component="span" sx={sx.tag}>Emojis: {profile.emojis}</Box>
    </Box>
    {!profile.isStarter && (
      <Box sx={sx.cardFooter}>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete(profile); }}
          sx={{ color: "#9CA3AF", "&:hover": { color: "#6B7280", backgroundColor: "#F3F4F6" } }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    )}
  </Box>
);

/* ── Company Knowledge Dialog ── */

const INITIAL_SIGNATURE_BLOCKS = [{ key: "signature_1", label: "Signature 1", value: "" }];

const sanitizeSignatureKey = (value, fallback = "signature_1") => {
  const raw = String(value || "").trim().toLowerCase();
  const cleaned = raw.replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || fallback;
};

const normalizeSignatureBlocks = (value) => {
  if (!Array.isArray(value) || value.length === 0) return INITIAL_SIGNATURE_BLOCKS;
  const used = new Set();
  const cleaned = value
    .map((item, idx) => {
      const baseKey = sanitizeSignatureKey(item?.key, `signature_${idx + 1}`);
      let key = baseKey;
      let n = 2;
      while (used.has(key)) {
        key = `${baseKey}_${n}`;
        n += 1;
      }
      used.add(key);
      const label = String(item?.label || `Signature ${idx + 1}`).trim();
      const body = String(item?.value || "");
      return { key, label, value: body };
    })
    .filter((item) => item.key);
  return cleaned.length ? cleaned : INITIAL_SIGNATURE_BLOCKS;
};

export const CompanyKnowledgeDialog = ({ open, onClose, onSaveCompanyKnowledge }) => {
  const user = useSelector((state) => state.auth.user);
  const [text, setText] = useState("");
  const [tab, setTab] = useState("knowledge");
  const [loading, setLoading] = useState(false);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [savingSignatures, setSavingSignatures] = useState(false);
  const [saved, setSaved] = useState(false);
  const [signatureBlocks, setSignatureBlocks] = useState(INITIAL_SIGNATURE_BLOCKS);
  const [selectedSignatureKey, setSelectedSignatureKey] = useState("signature_1");
  const [signatureStoreMeta, setSignatureStoreMeta] = useState({ domainId: "", targetField: "details", baseObject: {} });

  // Logo tab state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoDomainId, setLogoDomainId] = useState("");

  useEffect(() => {
    if (!open) return;
    setTab("knowledge");
    setSaved(false);
    getCompanyKnowledge().then((v) => setText(v));
  }, [open]);

  useEffect(() => {
    if (!open || !user?.domain) return;
    let cancelled = false;
    setLoadingSignatures(true);
    (async () => {
      try {
        const domainRecord = await apiGetDomainByName(user.domain);
        if (cancelled || !domainRecord) return;
        const detailsObj = domainRecord?.details && typeof domainRecord.details === "object" ? domainRecord.details : null;
        const dataObj = domainRecord?.data && typeof domainRecord.data === "object" ? domainRecord.data : null;
        const sourceObj = detailsObj || dataObj || {};
        const targetField = detailsObj ? "details" : (dataObj ? "data" : "details");

        const stored = sourceObj?.bulkEmail?.signatures;
        const selectedKey = sourceObj?.bulkEmail?.selectedSignatureKey;
        const normalized = normalizeSignatureBlocks(stored);
        setSignatureBlocks(normalized);
        setSelectedSignatureKey(
          normalized.some((sig) => sig.key === selectedKey)
            ? selectedKey
            : (normalized[0]?.key || "signature_1")
        );
        setSignatureStoreMeta({ domainId: domainRecord.id || "", targetField, baseObject: sourceObj && typeof sourceObj === "object" ? sourceObj : {} });

        setLogoDomainId(domainRecord.id || "");
        const avatar = domainRecord?.avatar;
        const publicBase = (config.URL.REACT_APP_PUBLIC_ASSETS_BASE_URL || "").replace(/\/+$/, "");
        if (avatar?.name) {
          const base = (avatar.base || publicBase).replace(/\/+$/, "");
          const dir = String(avatar.dir || "").replace(/^\/+|\/+$/g, "");
          const domainName = String(domainRecord?.name || user.domain || "").replace(/^\/+|\/+$/g, "");
          const parts = [base, domainName, dir, avatar.name].filter(Boolean);
          setCurrentAvatarUrl(parts.join("/"));
        } else {
          setCurrentAvatarUrl("");
        }
      } catch (err) {
        if (!cancelled) console.error("Failed loading signatures:", err);
      } finally {
        if (!cancelled) setLoadingSignatures(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user?.domain]);

  // Called by ThumbnailUploadWithEditor when the user finishes cropping.
  // We upload immediately to the public bucket and persist the avatar on the domain.
  const handleLogoCropped = async (croppedFile) => {
    if (!croppedFile || !logoDomainId) return;
    setUploadingLogo(true);
    try {
      const ext = (croppedFile.name.split(".").pop() || "").toLowerCase() || "png";
      const fileName = `${uuid_v4()}.${ext}`;
      const domainName = String(user?.domain || "").replace(/^\/+|\/+$/g, "");
      const dir = "config";
      const key = `${domainName}/${dir}/${fileName}`;
      await apiUploadFileToS3(key, croppedFile, {
        contentType: croppedFile.type || "image/png",
        isPublic: true,
      });
      const publicBase = (config.URL.REACT_APP_PUBLIC_ASSETS_BASE_URL || "").replace(/\/+$/, "");
      await apiUpdateDomainAvatar(logoDomainId, {
        name: fileName,
        dir,
        base: publicBase,
        mimetype: croppedFile.type || "image/png",
      });
      setCurrentAvatarUrl([publicBase, domainName, dir, fileName].join("/"));
      setLogoFile(croppedFile);
      // Tell the Sidebar to refetch domains so the new avatar shows everywhere.
      window.dispatchEvent(new CustomEvent("cs:domainsChanged"));
    } catch (err) {
      console.error("Failed to upload company logo:", err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!logoDomainId) return;
    setUploadingLogo(true);
    try {
      await apiUpdateDomainAvatar(logoDomainId, null);
      setCurrentAvatarUrl("");
      setLogoFile(null);
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl("");
      window.dispatchEvent(new CustomEvent("cs:domainsChanged"));
    } catch (err) {
      console.error("Failed to delete company logo:", err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const selectedSignatureToken = useMemo(() => {
    const selected = signatureBlocks.find((sig) => sig.key === selectedSignatureKey) || signatureBlocks[0];
    return selected?.key ? `{{${selected.key}}}` : "{{signature_1}}";
  }, [signatureBlocks, selectedSignatureKey]);

  const handleSave = async () => {
    setLoading(true);
    await saveCompanyKnowledge(text);
    if (typeof onSaveCompanyKnowledge === "function") {
      try {
        await onSaveCompanyKnowledge(text);
      } catch (err) {
        console.error("Failed to save company knowledge:", err);
      }
    }
    setLoading(false);
    setSaved(true);
  };

  const handleAddSignature = () => {
    setSignatureBlocks((prev) => {
      const nextIndex = prev.length + 1;
      const key = `signature_${nextIndex}`;
      const next = [...prev, { key, label: `Signature ${nextIndex}`, value: "" }];
      setSelectedSignatureKey(key);
      return next;
    });
  };

  const handleUpdateSignature = (key, field, value) => {
    if (field !== "key") {
      setSignatureBlocks((prev) => prev.map((sig) => (sig.key === key ? { ...sig, [field]: value } : sig)));
      return;
    }
    const nextKeyBase = sanitizeSignatureKey(value, key);
    setSignatureBlocks((prev) => {
      const existing = new Set(prev.map((sig) => sig.key).filter((k) => k !== key));
      let nextKey = nextKeyBase;
      let n = 2;
      while (existing.has(nextKey)) {
        nextKey = `${nextKeyBase}_${n}`;
        n += 1;
      }
      const next = prev.map((sig) => (sig.key === key ? { ...sig, key: nextKey } : sig));
      if (selectedSignatureKey === key) setSelectedSignatureKey(nextKey);
      return next;
    });
  };

  const handleRemoveSignature = (keyToRemove) => {
    setSignatureBlocks((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((sig) => sig.key !== keyToRemove);
      if (!next.some((sig) => sig.key === selectedSignatureKey)) {
        setSelectedSignatureKey(next[0]?.key || "signature_1");
      }
      return next;
    });
  };

  const handleSaveSignatures = async () => {
    if (!signatureStoreMeta.domainId) return;
    setSavingSignatures(true);
    try {
      const payload = {
        bulkEmail: {
          signatures: signatureBlocks,
          selectedSignatureKey,
          updatedAt: Date.now(),
        },
      };
      await apiUpdateDomainMetadata({
        id: signatureStoreMeta.domainId,
        [signatureStoreMeta.targetField]: { ...(signatureStoreMeta.baseObject || {}), ...payload },
      });
    } catch (err) {
      console.error("Failed saving signatures:", err);
    } finally {
      setSavingSignatures(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, height: "80vh", maxHeight: "80vh" } }}
    >
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5, mt: -0.5 }}>
          <Tab label="Company Knowledge" value="knowledge" />
          <Tab label="Signature Blocks" value="signatures" />
          <Tab label="Company Logo" value="logo" />
        </Tabs>
        {tab === "knowledge" && (
          <>
            <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2, lineHeight: 1.6 }}>
              Add information about your company, products, services, or key talking points.
              This context is automatically included in every email you compose with Magic.
            </Typography>
            <TextField
              fullWidth
              multiline
              value={text}
              onChange={(e) => { setText(e.target.value); setSaved(false); }}
              placeholder={"Example:\n\nCompany: Acme Corp\nWe build AI-powered sales tools that help teams close deals 2x faster.\n\nKey products:\n- Acme CRM — intelligent pipeline management\n- Acme Insights — real-time buyer intent signals\n\nDifferentiators:\n- 40% faster pipeline velocity vs competitors\n- SOC 2 Type II certified\n- Used by 500+ enterprise customers"}
              sx={{
                flex: 1,
                mb: 2,
                "& .MuiInputBase-root": { fontSize: 14, backgroundColor: "#fff", fontFamily: "inherit", alignItems: "flex-start", height: "100%" },
                "& .MuiInputBase-input": { height: "100% !important", overflow: "auto !important" },
              }}
            />
          </>
        )}
        {tab === "signatures" && (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.25, overflow: "hidden" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleAddSignature} sx={{ textTransform: "none", fontSize: 12 }}>
                Add signature
              </Button>
              <Button size="small" variant="outlined" onClick={handleSaveSignatures} disabled={savingSignatures || loadingSignatures || !signatureStoreMeta.domainId} sx={{ textTransform: "none", fontSize: 12 }}>
                {savingSignatures ? "Saving..." : "Save signatures"}
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#475569", minWidth: 38 }}>Use</Typography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <Select value={selectedSignatureKey} onChange={(e) => setSelectedSignatureKey(e.target.value)} sx={{ "& .MuiSelect-select": { fontSize: 13, py: 0.75 } }}>
                  {signatureBlocks.map((sig) => (
                    <MenuItem key={sig.key} value={sig.key} sx={{ fontSize: 13 }}>
                      {sig.key} ({sig.label || sig.key})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
              {signatureBlocks.map((sig) => (
                <Box key={sig.key} sx={{ p: 1, border: "1px solid #E5E7EB", borderRadius: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <TextField size="small" value={sig.key} onChange={(e) => handleUpdateSignature(sig.key, "key", e.target.value)} placeholder="signature_name" sx={{ width: 170, "& .MuiOutlinedInput-root": { fontSize: 12 } }} />
                    <TextField size="small" value={sig.label} onChange={(e) => handleUpdateSignature(sig.key, "label", e.target.value)} placeholder="Signature label" sx={{ width: 160, "& .MuiOutlinedInput-root": { fontSize: 12 } }} />
                    <IconButton size="small" onClick={() => handleRemoveSignature(sig.key)} disabled={signatureBlocks.length <= 1} sx={{ ml: "auto" }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <TextField fullWidth multiline minRows={2} value={sig.value} onChange={(e) => handleUpdateSignature(sig.key, "value", e.target.value)} placeholder={`Content for {{${sig.key}}}`} sx={{ "& .MuiOutlinedInput-root": { fontSize: 12 } }} />
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 11, color: "#64748B" }}>
              {loadingSignatures ? "Loading signatures from domain..." : `Saved location: domain.${signatureStoreMeta.targetField}.bulkEmail`}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#64748B" }}>
              Selected token for <code>{`{{signature}}`}</code>: <code>{selectedSignatureToken}</code>
            </Typography>
          </Box>
        )}
        {tab === "logo" && (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 2, overflow: "auto", alignItems: "center" }}>
            <Typography sx={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, textAlign: "center", maxWidth: 480 }}>
              Upload your company logo. Crop, zoom, and rotate, then save — it'll be stored in the
              public assets bucket and shown as the domain avatar throughout the app.
            </Typography>
            <Box sx={{ width: 280 }}>
              <ThumbnailUploadWithEditor
                url={currentAvatarUrl || null}
                variant="square"
                aspect={1}
                width={200}
                height={200}
                Icon={BusinessIcon}
                onChange={handleLogoCropped}
                onDelete={handleDeleteLogo}
              />
            </Box>
            {uploadingLogo && (
              <Typography sx={{ fontSize: 12, color: "#7C3AED" }}>Saving…</Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
            {tab === "knowledge"
              ? (text.length > 0 ? `${text.length} characters` : "No company knowledge saved")
              : tab === "signatures"
                ? `${signatureBlocks.length} signature block${signatureBlocks.length === 1 ? "" : "s"}`
                : (currentAvatarUrl ? "Logo saved" : "No logo uploaded")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: "#64748b" }}>
              Cancel
            </Button>
            {tab === "knowledge" && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || saved}
                sx={{
                  textTransform: "none", fontSize: 13, fontWeight: 600, px: 3,
                  backgroundColor: "#16a34a",
                  "&:hover": { backgroundColor: "#15803d" },
                  "&.Mui-disabled": { backgroundColor: saved ? "#16a34a" : "#e5e7eb", color: saved ? "#fff" : "#9ca3af" },
                }}
              >
                {saved ? "Saved!" : loading ? "Saving..." : "Save"}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

const VoiceProfileListPanel = ({
  loading,
  profiles,
  modeKind = "writing",
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  onSaveCompanyKnowledge,
  onClose,
}) => {
  const noun = modeKind === "text" ? "text" : "writing";
  const title = modeKind === "text" ? "Text Modes" : "Writing Modes";

  return (
  <MyMagicPanelShell
    onClose={onClose}
    titleContent={
      <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
        <AiIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{title}</Typography>
      </Box>
    }
    headerActions={
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" onClick={onAdd} title={`New ${noun} mode`} sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}>
          <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
        </IconButton>
      </Box>
    }
    footerLeft={
      <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
        {profiles.length} mode{profiles.length !== 1 ? "s" : ""}
      </Typography>
    }
  >
    {loading && (
      <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center", gap: 1.25 }}>
        <CircularProgress size={20} />
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Loading modes...</Typography>
      </Box>
    )}

    {!loading && profiles.length === 0 && (
      <Box sx={sx.empty}>
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{`No ${noun} modes yet.`}</Typography>
      </Box>
    )}

    {!loading && profiles.length > 0 && (
      <Box sx={sx.cardList}>
        {profiles.map((profile) => (
          <ProfileCard
            key={profile.id}
            profile={profile}
            selected={selectedId === profile.id}
            onClick={() => onSelect(profile)}
            onDelete={onDelete}
          />
        ))}
      </Box>
    )}
  </MyMagicPanelShell>
  );
};

export default VoiceProfileListPanel;
